import { FC, useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import {
  Autocomplete,
  Box,
  Button,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Add from "@mui/icons-material/Add";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { find, get } from "lodash";
import __ from "src/utils/Translations";
import { dynamicNotification } from "src/utils/Notifications";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { absencesEmployeeAbsenceRecordsWrite } from "src/utils/RoleBasedSecurity/permissions";
import { useSetAssingmentsForEmployee } from "src/Pages/Company/Absences/api/employee.queries";
import { getYearRange } from "src/utils/Company/Absences/checkExcludedWeekDays";
import {
  getInitialCalendars,
  transformCalendarsToOptions,
} from "src/utils/Company/Absences/transformCalendarsToOptions";
import {
  transformAbsenceTypesToOptions,
  getInitialPatterns,
  getAvailableAbsenceTypeOptions,
} from "src/utils/Company/Absences/transformAbsenceTypesToOptions";
import { Loader } from "src/Components/Loader/Loader";
import ActionButton from "src/Pages/Company/Absences/ActionButton/ActionButton";
import TooltipInfo from "src/Pages/Company/Absences/Forms/TooltipInfo";
import { ICalendar } from "src/Pages/Company/Absences/Calendars/Calendars.models";
import {
  IEmployeeAssignments,
  IEmployeeAssignmentsDTO,
  IPatternAssignment,
} from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecords.models";
import { IAbsenceType } from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import { absenceRecordsPatternsAndCalendarsFormSchema } from "./AbsenceRecordsPatternsAndCalendarsForm.schema";

interface IAbsenceRecordsCalendarsFormProps {
  employeeId: string;
  calendars: ICalendar[];
  absenceTypes: IAbsenceType[];
  assignmentsData: IEmployeeAssignments;
}

interface ICalendarField {
  year: string;
  calendarId: string | null;
}

interface IPatternField {
  absenceTypeId: string;
  patternId: string;
}

interface IFormValues {
  calendars: ICalendarField[];
  patterns: IPatternField[];
}

const transformToDTO = (
  formData: IFormValues,
  patternsData: IPatternAssignment[]
): IEmployeeAssignmentsDTO => {
  const calendars: Record<string, string | null> = {};
  const patterns: Record<string, string | null> = {};
  const existingAbsenceTypeIds = new Set(
    patternsData?.map((p) => p.absenceTypeId).filter(Boolean) || []
  );
  const formDataAbsenceTypeIds = new Set(
    formData.patterns?.map((p) => p.absenceTypeId).filter(Boolean)
  );

  formData.calendars?.forEach((calendar) => {
    calendars[calendar.year] = calendar.calendarId || null;
  });

  formData.patterns?.forEach((pattern) => {
    if (pattern.absenceTypeId && pattern.patternId) {
      patterns[pattern.absenceTypeId] = pattern.patternId;
    }
  });

  existingAbsenceTypeIds.forEach((absenceTypeId) => {
    if (!formDataAbsenceTypeIds.has(absenceTypeId)) {
      patterns[absenceTypeId] = null;
    }
  });

  return { calendars, patterns };
};

const AbsenceRecordsPatternsAndCalendarsForm: FC<
  IAbsenceRecordsCalendarsFormProps
> = ({ employeeId, calendars, absenceTypes, assignmentsData }) => {
  const history = useHistory();
  const canWriteAbsenceRecords = usePermission(
    absencesEmployeeAbsenceRecordsWrite
  );

  const currentYear = new Date().getFullYear().toString();
  const years = getYearRange(currentYear, 1);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const employeeCalendarsMutation = useSetAssingmentsForEmployee(employeeId);

  const sortedCalendars = useMemo(
    () => (calendars ? transformCalendarsToOptions(calendars) : {}),
    [calendars]
  );

  const absenceTypeOptions = useMemo(
    () => transformAbsenceTypesToOptions(absenceTypes),
    [absenceTypes]
  );

  const initialCalendars = useMemo(
    () => getInitialCalendars(assignmentsData.calendars, years),
    [assignmentsData, years]
  );

  const formMethods = useForm<IFormValues>({
    resolver: standardSchemaResolver(
      absenceRecordsPatternsAndCalendarsFormSchema
    ),
    defaultValues: {
      calendars: initialCalendars,
      patterns: assignmentsData.patterns,
    },
  });

  const { control, handleSubmit } = formMethods;

  const patternsWatch = useWatch({
    control,
    name: "patterns",
  });

  const {
    fields: patternsArray,
    remove: removePattern,
    append: appendPattern,
  } = useFieldArray({
    control,
    name: "patterns",
    keyName: "fieldId",
  });

  const {
    fields: calendarsArray,
    append: appendCalendar,
    prepend: prependCalendar,
  } = useFieldArray({
    control,
    name: "calendars",
    keyName: "fieldId",
  });

  const selectedAbsenceType = useCallback(
    (
      value: string,
      options: ReturnType<typeof getAvailableAbsenceTypeOptions>
    ) => (value ? find(options, { value }) : null),
    []
  );

  const selectedPattern = useCallback(
    (value: string, options: any[] = []) =>
      value ? find(options, { id: value }) : null,
    []
  );

  const selectedCalendar = useCallback(
    (value: string | null, year: string) =>
      value?.length
        ? (sortedCalendars[year] || []).find((o) => o.id === value)
        : null,
    [sortedCalendars]
  );

  const onSubmit = (formData: IFormValues) => {
    if (!canWriteAbsenceRecords) return;

    const apiPayload = transformToDTO(formData, assignmentsData.patterns);

    employeeCalendarsMutation.mutate(apiPayload, {
      onSuccess: () => {
        dynamicNotification(
          __("Pomyślnie zapisano kalendarze i wzorce pracownika.")
        );
      },
      onError: () => {
        dynamicNotification(
          __("Nie udało się zapisać kalendarzy i wzorców pracownika."),
          "error"
        );
      },
    });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddPreviousYear = () => {
    if (calendarsArray.length > 0) {
      const firstYear = parseInt(calendarsArray[0].year, 10);
      const previousYear = (firstYear - 1).toString();

      prependCalendar({
        year: previousYear,
        calendarId: "",
      });
    }
    handleMenuClose();
  };

  const handleAddNextYear = () => {
    if (calendarsArray.length > 0) {
      const lastYear = parseInt(
        calendarsArray[calendarsArray.length - 1].year,
        10
      );
      const nextYear = (lastYear + 1).toString();

      appendCalendar({
        year: nextYear,
        calendarId: "",
      });
    }
    handleMenuClose();
  };

  return (
    <FormProvider {...formMethods}>
      {employeeCalendarsMutation.isPending && <Loader />}
      <form
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        data-testid="absence-records-calendars-form"
      >
        {/* Patterns Section */}
        <Typography variant="subtitle1" component="h2" sx={{ mb: 2 }}>
          {__("Przypisz wzorce urlopowe")}
          <TooltipInfo
            title={__(
              "Edycja wzorca jest zawsze możliwa. W przypadku gdy zmniejszamy liczbę dni na wzorcu, zostaną anulowane wnioski pracownika przypadające na najbardziej odległe terminy."
            )}
          />
        </Typography>

        {patternsArray.map((field, index) => (
          <Stack key={field.fieldId} direction="row" sx={{ gap: 2, mb: 2 }}>
            <Controller
              name={`patterns.${index}.absenceTypeId`}
              control={control}
              render={({
                field: { value, onChange, ref },
                fieldState: { invalid, error },
              }) => {
                const selectedAbsenceTypeIds =
                  patternsWatch?.map((p) => p.absenceTypeId) || [];
                const availableOptions = getAvailableAbsenceTypeOptions(
                  absenceTypeOptions,
                  selectedAbsenceTypeIds,
                  index,
                  value
                );

                return (
                  <Autocomplete
                    fullWidth
                    disablePortal
                    options={availableOptions}
                    value={selectedAbsenceType(value, availableOptions)}
                    onChange={(__, newValue) => {
                      onChange(newValue?.value || "");
                    }}
                    disabled={!canWriteAbsenceRecords}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={__("Nazwa Rodzaju Nieobecności")}
                        margin="normal"
                        error={invalid}
                        helperText={error?.message}
                        inputRef={ref}
                        placeholder={__("Wybierz rodzaj nieobecności")}
                        disabled={!canWriteAbsenceRecords}
                        data-testid={`absence-records-patterns-form-field__absence-type-${index}`}
                      />
                    )}
                  />
                );
              }}
            />
            <Controller
              name={`patterns.${index}.patternId`}
              control={control}
              render={({
                field: { value, onChange, ref },
                fieldState: { invalid, error },
              }) => {
                const currentAbsenceTypeId = get(
                  patternsWatch,
                  [index, "absenceTypeId"],
                  ""
                );
                const currentAbsenceType = find(absenceTypeOptions, {
                  absenceTypeId: currentAbsenceTypeId,
                });
                const options = get(currentAbsenceType, "patterns", []);

                return (
                  <Autocomplete
                    fullWidth
                    disablePortal
                    options={options}
                    disabled={!canWriteAbsenceRecords || !options}
                    value={selectedPattern(value, options)}
                    onChange={(__, newValue) => {
                      onChange(get(newValue, "id", ""));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={__("Wzorzec")}
                        placeholder={__("Wybierz wzorzec")}
                        margin="normal"
                        error={invalid}
                        helperText={error?.message}
                        inputRef={ref}
                        disabled={!canWriteAbsenceRecords}
                        slotProps={{
                          ...params.slotProps,
                          inputLabel: {
                            ...(params.slotProps?.inputLabel as object),
                            shrink: true,
                          },
                        }}
                        required
                        data-testid={`absence-records-patterns-form-field__pattern-${index}`}
                      />
                    )}
                  />
                );
              }}
            />
            {canWriteAbsenceRecords && (
              <ActionButton
                label={__("Usuń")}
                bgColor="error.main"
                bgColorHover="error.dark"
                color="white"
                onClick={() => removePattern(index)}
                customStyles={{
                  mt: "1.5rem",
                }}
                data-testid={`absence-records-patterns-form__button-delete-${index}`}
              >
                <DeleteOutlined />
              </ActionButton>
            )}
          </Stack>
        ))}

        {canWriteAbsenceRecords && (
          <Button
            variant="text"
            onClick={() =>
              appendPattern({
                absenceTypeId: "",
                patternId: "",
              })
            }
            sx={{ mb: 4 }}
            data-testid="absence-records-patterns-form__button-add"
          >
            <Add
              sx={{
                fontSize: "1rem",
                bgcolor: "info.main",
                color: "white",
                marginRight: "0.5rem",
                borderRadius: "4px",
              }}
            />
            {__("Przypisz kolejny wzorzec")}
          </Button>
        )}

        {/* Calendars Section */}
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            mt: 4,
          }}
        >
          <Typography variant="subtitle1" component="h2">
            {__("Przypisz kalendarze urlopowe")}
            <TooltipInfo
              title={__(
                "Po złożeniu wniosków na dany rok, brak możliwości usunięcia kalendarzy. W tym wypadku możliwa jest tylko zmiana kalendarzy pracownika - nieodbyte wnioski ulegają przeliczeniu zgodnie z nowym kalendarzem."
              )}
            />
          </Typography>
          {canWriteAbsenceRecords && (
            <>
              <Button
                onClick={handleMenuOpen}
                variant="outlined"
                endIcon={<ArrowDropDownIcon />}
                data-testid="absence-records-calendars-form__menu-button"
              >
                {__("Dodaj kalendarz")}
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                data-testid="absence-records-calendars-form__menu"
              >
                <MenuItem
                  onClick={handleAddPreviousYear}
                  data-testid="absence-records-calendars-form__menu-previous-year"
                >
                  {__("Poprzedni rok")}
                </MenuItem>
                <MenuItem
                  onClick={handleAddNextYear}
                  data-testid="absence-records-calendars-form__menu-next-year"
                >
                  {__("Następny rok")}
                </MenuItem>
              </Menu>
            </>
          )}
        </Stack>

        {calendarsArray.map((field, index) => (
          <Stack
            key={field.fieldId}
            direction="row"
            sx={{ alignItems: "center", gap: 2, mb: 2 }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" sx={{ mb: 2, fontWeight: "medium" }}>
                {__("Na rok:")} <strong>{field.year}</strong>
                {field.year === currentYear && (
                  <Typography component="span" color="primary.dark">
                    {" "}
                    ({__("aktualny")})
                  </Typography>
                )}
              </Typography>
              <Controller
                name={`calendars.${index}.calendarId`}
                control={control}
                render={({
                  field: { value, onChange, ref },
                  fieldState: { invalid, error },
                }) => {
                  const selectedOption =
                    selectedCalendar(value, field.year) || null;
                  const isClearable =
                    assignmentsData.calendars?.find(
                      (c) => c.year === field.year
                    )?.isClearable ?? true;

                  return (
                    <Autocomplete
                      fullWidth
                      disablePortal
                      disableClearable={!isClearable}
                      disabled={
                        !sortedCalendars[field.year] || !canWriteAbsenceRecords
                      }
                      options={sortedCalendars[field.year] || []}
                      value={selectedOption}
                      onChange={(_event, newValue) => {
                        onChange(newValue?.id || "");
                      }}
                      onInputChange={
                        !isClearable
                          ? (_event, _inputValue, reason) => {
                              if (reason === "clear" || reason === "reset") {
                                // preserve the current value — do nothing
                              }
                            }
                          : undefined
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={__("Nazwa kalendarza")}
                          error={invalid}
                          helperText={error?.message}
                          inputRef={ref}
                          placeholder={
                            !sortedCalendars[field.year]
                              ? __("Brak kalendarzy na ten rok")
                              : __("Wybierz kalendarz")
                          }
                          slotProps={{
                            ...params.slotProps,
                            inputLabel: {
                              ...(params.slotProps?.inputLabel as object),
                              shrink: true,
                            },
                          }}
                          data-testid={`absence-records-calendars-form-field__calendar-${index}`}
                        />
                      )}
                    />
                  );
                }}
              />
            </Box>
          </Stack>
        ))}

        {/* Form Actions */}
        {canWriteAbsenceRecords && (
          <Box
            sx={{
              textAlign: "right",
              margin: "2rem 0 0 1rem",
            }}
          >
            <Button
              variant="outlined"
              type="button"
              size="large"
              sx={{ marginRight: "1rem" }}
              onClick={() => history.goBack()}
              disabled={employeeCalendarsMutation.isPending}
            >
              {__("Anuluj")}
            </Button>
            <Button
              variant="contained"
              type="submit"
              size="large"
              disabled={employeeCalendarsMutation.isPending}
              data-testid="absence-records-calendars-form__submit-button"
            >
              {__("Zapisz ustawienia")}
            </Button>
          </Box>
        )}
      </form>
    </FormProvider>
  );
};

export default AbsenceRecordsPatternsAndCalendarsForm;
